import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Shared themed modal shell: portal + overlay (click-outside to close), Escape
// handling, body-scroll lock, autofocus of the first field, and a title/message
// header plus a Cancel/Confirm footer. Callers supply the body fields as
// children and an onSubmit handler for the form. `danger` turns the confirm
// button red for destructive actions; `showCancel={false}` gives a
// single-button (alert-style) dialog, and `showConfirm={false}` leaves only
// Cancel in the footer, for a dialog whose choices are buttons in the body.
// `widthClass` widens the shell for a dialog that has a list or a paste box in
// it rather than a handful of fields.
const BaseModal = ({
  open,
  titleId,
  title,
  message,
  onSubmit,
  onCancel,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  bodyClassName = 'px-6 py-4',
  widthClass = 'max-w-md',
  selectOnOpen = false,
  danger = false,
  showCancel = true,
  showConfirm = true,
  children,
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return undefined;
    const root = containerRef.current;
    // Field-less dialogs (confirm/alert) focus a footer button instead: the
    // safe Cancel for destructive confirms, otherwise the confirm button. Cancel
    // is found by its own marker rather than by being the only plain button —
    // a dialog that puts its choices in the body has several.
    const field =
      root?.querySelector('input, select, textarea') ||
      root?.querySelector(danger && showCancel ? '[data-modal-cancel]' : 'button[type="submit"]') ||
      root?.querySelector('[data-modal-cancel]');
    if (!field) return undefined;
    const id = window.setTimeout(() => {
      field.focus();
      if (selectOnOpen && typeof field.select === 'function') field.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, selectOnOpen, danger, showCancel]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        ref={containerRef}
        className={`w-full ${widthClass} bg-gray-800/95 backdrop-blur-sm border border-purple-500/20 rounded-xl shadow-2xl`}
      >
        <form onSubmit={onSubmit}>
          <div className="px-6 pt-6 pb-2">
            <h2 id={titleId} className="text-xl font-semibold text-white">
              {title}
            </h2>
            {message && <p className="mt-2 text-sm text-gray-400">{message}</p>}
          </div>

          {children != null && <div className={bodyClassName}>{children}</div>}

          <div className="px-6 pb-6 pt-2 flex justify-end space-x-3">
            {showCancel && (
              <button
                type="button"
                data-modal-cancel
                onClick={onCancel}
                className="bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white px-5 py-2.5 rounded-lg font-medium transition-all duration-200 border border-gray-600 hover:border-gray-500"
              >
                {cancelLabel}
              </button>
            )}
            {showConfirm && (
              <button
                type="submit"
                className={`bg-gradient-to-r ${
                  danger
                    ? 'from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600'
                    : 'from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600'
                } text-white font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg`}
              >
                {confirmLabel}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default BaseModal;
